import React, {forwardRef, MouseEvent, Ref, useCallback, useImperativeHandle, useState} from "react"
import styles from '../css/App.module.css'
import ModalColorPicker from "./ModalColorPicker";
import Icon from "./Icon";
import icons from "../css/Icon.module.css";
import ModalDialog, {ModalAction} from "./ModalDialog";
import ModalTextInput from "./ModalTextInput";
import LoadingSpinner from "../components/LoadingSpinner";
import classNames from "classnames";

interface Props {
    id: string,
    onCurtainClick?: (result: boolean) => void,
    showCurtain?: boolean,
    showLoadingIndicator?: boolean,
    setIsPreview?: (v: boolean) => void
    modalTextInputValidation: (v: string) => boolean
}

export interface ModalsRefProps {
    showActionsModal: (options: Array<ModalAction>) => void,
    hideActionModal: () => void,
    showColorPickerModal: (title: string, onConfirmColorPicker: ((result: string) => void), currentColor: string) => void,
    showTextInputModal: (onConfirmTextInput: ((result: string) => void)) => void
}

export default forwardRef<ModalsRefProps, Props>((props: Props, ref: Ref<ModalsRefProps>) => {

    //Props

    const {
        onCurtainClick,
        showLoadingIndicator,
        setIsPreview,
        modalTextInputValidation,
        showCurtain = false,
        id,
    } = props;

    // State

    const [showModal, setShowModal] = useState(false);
    const [showTextModal, setShowTextModal] = useState(false);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [prevColor, setPrevColor] = useState('');
    const [colorPickerTitle, setColorPickerTitle] = useState('');
    const [modalActions, setModalActions] = useState(Array<ModalAction>());
    const [onConfirmTextInput, setOnConfirmTextInput] = useState<((result: string) => void) | undefined>(undefined);
    const [onConfirmColorPicker, setOnConfirmColorPicker] = useState<((result: string) => void) | undefined>(undefined);

    // Imperative API

    useImperativeHandle(ref, () => ({
        showActionsModal: (options: Array<ModalAction>) => {
            setModalActions(options.map(option => {

                if (option.keepModalShown) {
                    return option;
                }

                const originalOnChange = option.onChange;
                const originalOnClick = option.onClick;

                option.onChange = (file: File) => {
                    setShowModal(false);
                    originalOnChange && originalOnChange(file);
                };

                option.onClick = (event: MouseEvent) => {
                    setShowModal(false);
                    originalOnClick && originalOnClick(event);
                };

                return option;
            }));
            setShowModal(true);
        },
        hideActionModal: () => {
            setShowModal(false);
        },
        showColorPickerModal: (title: string, onConfirmColorPicker: ((result: string) => void), currentColor: string) => {
            setPrevColor(currentColor);
            setColorPickerTitle(title);
            setOnConfirmColorPicker(() => {
                return onConfirmColorPicker
            });
            setShowColorPicker(true);
        },
        showTextInputModal: (onConfirmTextInput: ((result: string) => void)) => {
            setOnConfirmTextInput(() => {
                return onConfirmTextInput;
            });
            setShowTextModal(true);
        },
    }));

    // Callbacks

    const handleDismissColorPicker = () => {
        onConfirmColorPicker && onConfirmColorPicker(prevColor);
        setShowColorPicker(false);
    };

    const changeModalVisibility = useCallback(
        (e) => {
            setShowModal(v => !v)
        },
        [],
    );

    const changeTextModalVisibility = useCallback(
        (e) => {
            setShowTextModal(v => !v)
        },
        [],
    );

    const _onConfirmTextInput = useCallback(
        (value: string) => {
            setShowTextModal(v => !v);
            setShowModal(v => !v);
            onConfirmTextInput && onConfirmTextInput(value)
        },
        [onConfirmTextInput],
    );

    const _onValidate = useCallback(
        (v: string) => {
            setShowColorPicker(v => !v);
            onConfirmColorPicker && onConfirmColorPicker(v);
        },
        [onConfirmColorPicker],
    );

    const handleOnChange = useCallback(
        (v: string) => {
            onConfirmColorPicker && onConfirmColorPicker(v);
        },
        [onConfirmColorPicker],
    );

    const handleOnUserStartInteracting = useCallback(
        (v: boolean) => {
            setIsPreview && setIsPreview(v)
        },
        [setIsPreview],
    );

    return (
        <>
            {
                showCurtain &&
                <div onClick={onCurtainClick && onCurtainClick.bind(null, false)} className={styles.curtain}/>
            }

            {
                showLoadingIndicator &&
                <div className={classNames(styles.absoluteCenter, styles.centerItems)}>
                  <LoadingSpinner xl/>
                </div>
            }

            <ModalColorPicker
                onValidate={_onValidate}
                onChange={handleOnChange}
                defaultColor={prevColor}
                onUserStartInteracting={handleOnUserStartInteracting}
                titleProps={{title: colorPickerTitle}}
                id={`${id}-modal-color-picker`}
                buttonProps={{
                    primary: true,
                    children: <Icon white name={icons.iconTick}/>
                }}
                onDismiss={handleDismissColorPicker}
                show={showColorPicker}/>

            <ModalDialog
                actions={modalActions || []}
                id={`${id}-modal-dialog`}
                show={showModal || showLoadingIndicator}
                onDismiss={changeModalVisibility}/>

            <ModalTextInput
                id={`${id}-modal-text-input`}
                onDismiss={changeTextModalVisibility}
                onConfirm={_onConfirmTextInput}
                isValid={modalTextInputValidation}
                show={showTextModal}/>
        </>
    );
});
